import api, { API_URL, API_VERSION } from './api';
import type {
  PaymentServiceType,
  Service,
  FavoriteService,
  SavedReference,
  RecentPayment,
  QRPaymentData
} from '../types';

// Base URL for the new REST API
const PAYMENT_API_BASE = `${API_URL}/${API_VERSION}`;

// LocalStorage keys
const STORAGE_KEYS = {
  FAVORITES: 'pagali_favorites',
  SAVED_REFERENCES: 'pagali_saved_references',
  RECENT_PAYMENTS: 'pagali_recent_payments',
};

class PaymentServiceClient {
  /**
   * Initiate a service payment via REST API
   * Returns a payment URL for SISP redirect
   */
  async initiatePayment(params: {
    serviceId: string;
    serviceType: PaymentServiceType;
    amount: number;
    reference: string;
    entityCode?: string;
    walletCardId?: string;
    paymentMethod?: string;
    descricao?: string;
    returnUrl?: string;
  }): Promise<{ success: boolean; paymentUrl?: string; transactionId?: string; merchantRef?: string; paymentMethod?: string; error?: string }> {
    try {
      const response = await api.post<{
        success: boolean;
        data?: {
          paymentUrl?: string;
          transactionId?: string;
          merchantRef?: string;
          paymentMethod?: string;
          transactionRef?: string;
          message?: string;
        };
        error?: string;
      }>('/client/payments/initiate', {
        servicoId: params.serviceId,
        tipoServico: params.serviceType,
        montante: params.amount,
        referencia: params.reference,
        entidade: params.entityCode,
        walletCardId: params.walletCardId,
        paymentMethod: params.paymentMethod,
        descricao: params.descricao,
        returnUrl: params.returnUrl,
      });

      if (response.data.success && response.data.data) {
        return {
          success: true,
          paymentUrl: response.data.data.paymentUrl,
          transactionId: response.data.data.transactionId || response.data.data.transactionRef,
          merchantRef: response.data.data.merchantRef,
          paymentMethod: response.data.data.paymentMethod,
        };
      }

      return {
        success: false,
        error: response.data.error || 'Falha ao iniciar pagamento',
      };
    } catch (error) {
      console.error('Payment initiation error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao iniciar pagamento',
      };
    }
  }

  /**
   * Get payment status by transaction ID
   */
  async getPaymentStatus(transactionId: string): Promise<{
    success: boolean;
    status?: 'pending' | 'completed' | 'failed' | 'refunded';
    montante?: number;
    referencia?: string;
  }> {
    try {
      const response = await api.get<{
        success: boolean;
        data?: {
          status: 'pending' | 'completed' | 'failed' | 'refunded';
          montante: number;
          referencia: string;
        };
        error?: string;
      }>(`/client/payments/${transactionId}/status`);

      if (response.data.success && response.data.data) {
        return {
          success: true,
          status: response.data.data.status,
          montante: response.data.data.montante,
          referencia: response.data.data.referencia,
        };
      }

      return { success: false };
    } catch (error) {
      console.error('Get payment status error:', error);
      return { success: false };
    }
  }

  /**
   * Get payment status by merchantRef (for callback result page)
   */
  async getPaymentStatusByRef(merchantRef: string): Promise<{
    success: boolean;
    status?: 'pending' | 'completed' | 'failed' | 'refunded';
    montante?: number;
    referencia?: string;
    entidade?: string;
    tipoServico?: string;
    tokenRecarga?: string;
    talao?: string;
  }> {
    try {
      const response = await api.get<{
        success: boolean;
        data?: {
          status: 'pending' | 'completed' | 'failed' | 'refunded';
          montante: number;
          referencia: string;
          entidade?: string;
          tipoServico?: string;
          tokenRecarga?: string;
          talao?: string;
        };
        error?: string;
      }>(`/client/payments/by-ref/${merchantRef}/status`);

      if (response.data.success && response.data.data) {
        return {
          success: true,
          status: response.data.data.status,
          montante: response.data.data.montante,
          referencia: response.data.data.referencia,
          entidade: response.data.data.entidade,
          tipoServico: response.data.data.tipoServico,
          tokenRecarga: response.data.data.tokenRecarga,
          talao: response.data.data.talao,
        };
      }

      return { success: false };
    } catch (error) {
      console.error('Get payment status by ref error:', error);
      return { success: false };
    }
  }

  /**
   * Build payment page URL using the new REST API
   */
  buildPaymentUrl(params: {
    idTemp: string;
    userId: string;
    tipoServico: PaymentServiceType;
    montante: string;
    referencia: string;
    entidade?: string;
    idCardWallet?: string;
    tickets?: Array<{ id: string; quant: number }>;
    products?: Array<{ id: string; quant: number }>;
  }): string {
    const { idTemp, userId, tipoServico, montante, referencia, entidade, idCardWallet, tickets, products } = params;

    // Build URL using the new REST API payment endpoint
    const baseUrl = `${PAYMENT_API_BASE}/client/payments/page`;
    const searchParams = new URLSearchParams();

    searchParams.append('id_temp', idTemp);
    searchParams.append('id_user', userId);
    searchParams.append('tipo_servico', tipoServico);
    searchParams.append('montante', montante);

    switch (tipoServico) {
      case 'EVT':
        if (tickets) {
          searchParams.append('tickets', JSON.stringify(tickets));
        }
        break;
      case 'PSHOP':
        if (products) {
          searchParams.append('produtos', JSON.stringify(products));
        }
        break;
      case 'EST':
        if (entidade) searchParams.append('entidade', entidade);
        searchParams.append('referencia', referencia);
        break;
      default:
        searchParams.append('referencia', referencia);
    }

    if (idCardWallet) {
      searchParams.append('id_card_wallet', idCardWallet);
    }

    return `${baseUrl}?${searchParams.toString()}`;
  }

  /**
   * Build cart payment URL
   */
  buildCartPaymentUrl(params: {
    userId: string;
    montante: string;
    cart: Array<{ id: string; quant: number }>;
    idCardWallet?: string;
  }): string {
    const { userId, montante, cart, idCardWallet } = params;

    const baseUrl = `${PAYMENT_API_BASE}/client/payments/cart`;
    const searchParams = new URLSearchParams();

    searchParams.append('id_user', userId);
    searchParams.append('tipo_servico', 'PSHOP');
    searchParams.append('products', JSON.stringify(cart));
    searchParams.append('montante', montante);

    if (idCardWallet) {
      searchParams.append('id_card_wallet', idCardWallet);
    }

    return `${baseUrl}?${searchParams.toString()}`;
  }

  /**
   * Build add wallet card URL
   */
  buildAddWalletUrl(clientId: string, montante: string): string {
    return `${PAYMENT_API_BASE}/client/wallet/tokenize?id_client=${clientId}&montante=${montante}`;
  }

  /**
   * Build receipt URL
   */
  buildReceiptUrl(invoiceId: string): string {
    return `${PAYMENT_API_BASE}/client/payments/${invoiceId}/receipt`;
  }

  /**
   * Get user session data from storage
   */
  getSessionData(): { userId: string; clientId: string } | null {
    const userSession = sessionStorage.getItem('auth');
    if (!userSession) return null;

    try {
      const userData = JSON.parse(userSession);
      return {
        userId: userData.cliente?.USER_ID || userData.user?.id || '',
        clientId: userData.cliente?.ID || userData.user?.clientId || '',
      };
    } catch {
      return null;
    }
  }

  /**
   * Get services by category for payment selection
   */
  async getServicesByCategory(categoryId: string): Promise<Service[]> {
    const response = await api.get<{
      success: boolean;
      data?: Service[];
      error?: string;
    }>(`/public/servicos?categoria=${categoryId}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar serviços');
  }

  /**
   * Get service details
   */
  async getServiceDetails(serviceId: string): Promise<Service> {
    const response = await api.get<{
      success: boolean;
      data?: Service;
      error?: string;
    }>(`/public/servicos/${serviceId}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar detalhes do serviço');
  }

  // ============================================
  // FAVORITES MANAGEMENT
  // ============================================

  getFavorites(): FavoriteService[] {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.FAVORITES);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  addFavorite(service: {
    serviceId: string;
    serviceName: string;
    serviceLogo?: string;
    serviceMimeType?: string;
    categoryId: string;
    categoryName: string;
  }): FavoriteService {
    const favorites = this.getFavorites();
    const newFavorite: FavoriteService = {
      id: `fav_${Date.now()}`,
      ...service,
      addedAt: new Date().toISOString(),
    };
    favorites.unshift(newFavorite);
    localStorage.setItem(STORAGE_KEYS.FAVORITES, JSON.stringify(favorites));
    return newFavorite;
  }

  removeFavorite(serviceId: string): void {
    const favorites = this.getFavorites().filter(f => f.serviceId !== serviceId);
    localStorage.setItem(STORAGE_KEYS.FAVORITES, JSON.stringify(favorites));
  }

  isFavorite(serviceId: string): boolean {
    return this.getFavorites().some(f => f.serviceId === serviceId);
  }

  // ============================================
  // SAVED REFERENCES MANAGEMENT
  // ============================================

  // In-memory cache (loaded from API)
  private _savedRefsCache: SavedReference[] | null = null;

  async loadSavedReferences(): Promise<SavedReference[]> {
    try {
      const { data } = await api.get('/client/saved-references');
      if (data.success) {
        this._savedRefsCache = (data.data || []).map((r: any) => ({
          id: String(r.id),
          serviceId: '',
          serviceName: r.servico_nome || '',
          label: r.label,
          referencia: r.referencia,
          entidade: r.entidade,
          tipoServico: r.tipo_servico || 'RCG',
          createdAt: r.created_at,
        }));
      }
    } catch {
      this._savedRefsCache = [];
    }
    return this._savedRefsCache || [];
  }

  getSavedReferences(): SavedReference[] {
    // Return cache; if not loaded, return localStorage fallback and trigger async load
    if (this._savedRefsCache !== null) return this._savedRefsCache;
    try {
      const data = localStorage.getItem(STORAGE_KEYS.SAVED_REFERENCES);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  async addSavedReference(ref: {
    serviceId: string;
    serviceName: string;
    serviceLogo?: string;
    serviceMimeType?: string;
    label: string;
    referencia: string;
    entidade?: string;
    tipoServico: PaymentServiceType;
  }): Promise<SavedReference> {
    try {
      await api.post('/client/saved-references', {
        label: ref.label,
        referencia: ref.referencia,
        entidade: ref.entidade,
        tipoServico: ref.tipoServico,
        servicoNome: ref.serviceName,
      });
    } catch { /* fallback to local */ }

    const newRef: SavedReference = {
      id: `ref_${Date.now()}`,
      ...ref,
      createdAt: new Date().toISOString(),
    };
    // Update cache and persist to localStorage
    if (this._savedRefsCache) {
      this._savedRefsCache.unshift(newRef);
      localStorage.setItem(STORAGE_KEYS.SAVED_REFERENCES, JSON.stringify(this._savedRefsCache));
    } else {
      let local: SavedReference[] = [];
      try {
        const data = localStorage.getItem(STORAGE_KEYS.SAVED_REFERENCES);
        local = data ? JSON.parse(data) : [];
      } catch { /* ignore */ }
      local.unshift(newRef);
      localStorage.setItem(STORAGE_KEYS.SAVED_REFERENCES, JSON.stringify(local));
    }
    return newRef;
  }

  async updateSavedReference(id: string, updates: Partial<SavedReference>): Promise<void> {
    try {
      await api.put(`/client/saved-references/${id}`, { label: updates.label, referencia: updates.referencia });
    } catch { /* ignore */ }
    if (this._savedRefsCache) {
      this._savedRefsCache = this._savedRefsCache.map(r => r.id === id ? { ...r, ...updates } : r);
    }
    const references = this.getSavedReferences().map(ref =>
      ref.id === id ? { ...ref, ...updates } : ref
    );
    localStorage.setItem(STORAGE_KEYS.SAVED_REFERENCES, JSON.stringify(references));
  }

  async removeSavedReference(id: string): Promise<void> {
    try {
      await api.delete(`/client/saved-references/${id}`);
    } catch { /* ignore */ }
    if (this._savedRefsCache) {
      this._savedRefsCache = this._savedRefsCache.filter(r => r.id !== id);
    }
    const references = this.getSavedReferences().filter(r => r.id !== id);
    localStorage.setItem(STORAGE_KEYS.SAVED_REFERENCES, JSON.stringify(references));
  }

  // ============================================
  // RECENT PAYMENTS MANAGEMENT
  // ============================================

  getRecentPayments(limit = 10): RecentPayment[] {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.RECENT_PAYMENTS);
      const payments: RecentPayment[] = data ? JSON.parse(data) : [];
      return payments.slice(0, limit);
    } catch {
      return [];
    }
  }

  addRecentPayment(payment: {
    serviceId: string;
    serviceName: string;
    serviceLogo?: string;
    serviceMimeType?: string;
    referencia: string;
    entidade?: string;
    montante: number;
    tipoServico: PaymentServiceType;
    status: 'success' | 'failed' | 'pending';
  }): RecentPayment {
    const payments = this.getRecentPayments(50); // Keep max 50
    const newPayment: RecentPayment = {
      id: `pay_${Date.now()}`,
      ...payment,
      paidAt: new Date().toISOString(),
    };
    payments.unshift(newPayment);
    localStorage.setItem(STORAGE_KEYS.RECENT_PAYMENTS, JSON.stringify(payments.slice(0, 50)));
    return newPayment;
  }

  clearRecentPayments(): void {
    localStorage.removeItem(STORAGE_KEYS.RECENT_PAYMENTS);
  }

  // ============================================
  // QR CODE PARSING
  // ============================================

  parseQRCode(qrData: string): QRPaymentData | null {
    try {
      // 1. Official vinti4 QR format (BT202211A):
      // ENTIDADE:99999;REFERENCIA:123456789;MONTANTE:500,00;DATALIMITEPAGAMENTO:DD-MM-AAAA;...SISTEMA:vinti4
      if (qrData.includes('ENTIDADE:') && qrData.includes('REFERENCIA:')) {
        const fields: Record<string, string> = {};
        const parts = qrData.split(';');

        for (const part of parts) {
          const colonIdx = part.indexOf(':');
          if (colonIdx > 0) {
            const key = part.substring(0, colonIdx).trim().toUpperCase();
            const value = part.substring(colonIdx + 1).trim();
            fields[key] = value;
          }
        }

        return {
          entidade: fields['ENTIDADE'] || undefined,
          referencia: fields['REFERENCIA'] || '',
          montante: fields['MONTANTE'] ? parseFloat(fields['MONTANTE'].replace(',', '.')) : undefined,
          tipoServico: 'EST',
        };
      }

      // 2. JSON format (structured QR)
      if (qrData.startsWith('{')) {
        const parsed = JSON.parse(qrData);
        return {
          entidade: parsed.entidade || parsed.entity,
          referencia: parsed.referencia || parsed.reference || parsed.ref,
          montante: parsed.montante || parsed.amount,
          serviceId: parsed.serviceId || parsed.service_id,
          tipoServico: parsed.tipoServico || parsed.tipo_servico || 'WBS',
        };
      }

      // 3. Pipe-separated format: ENTIDADE|REFERENCIA|MONTANTE
      if (qrData.includes('|')) {
        const parts = qrData.split('|');
        if (parts.length >= 2) {
          return {
            entidade: parts[0] || undefined,
            referencia: parts[1],
            montante: parts[2] ? parseFloat(parts[2]) : undefined,
            tipoServico: 'EST',
          };
        }
      }

      // 4. Simple numeric reference
      if (/^\d{6,15}$/.test(qrData.trim())) {
        return {
          referencia: qrData.trim(),
          tipoServico: 'WBS',
        };
      }

      return null;
    } catch {
      return null;
    }
  }

  // ============================================
  // SEARCH SERVICES
  // ============================================

  async searchServices(query: string): Promise<Service[]> {
    if (!query || query.length < 2) return [];

    try {
      const response = await api.get<{
        success: boolean;
        data?: Service[];
        error?: string;
      }>(`/public/servicos/search?q=${encodeURIComponent(query)}`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return [];
    } catch {
      return [];
    }
  }

  // ============================================
  // BILL ESTIMATION
  // ============================================

  /**
   * Get bill estimate based on payment history for a specific service/reference
   */
  getBillEstimate(serviceId: string, referencia?: string): {
    average: number;
    min: number;
    max: number;
    lastPayment: number;
    count: number;
    trend: 'up' | 'down' | 'stable';
  } | null {
    const recentPayments = this.getRecentPayments(50);

    // Filter payments for this service and optionally by reference
    const relevantPayments = recentPayments.filter(p => {
      if (p.serviceId !== serviceId) return false;
      if (referencia && p.referencia !== referencia) return false;
      return p.status === 'success';
    });

    if (relevantPayments.length === 0) return null;

    const amounts = relevantPayments.map(p => p.montante);
    const sum = amounts.reduce((a, b) => a + b, 0);
    const average = Math.round(sum / amounts.length);
    const min = Math.min(...amounts);
    const max = Math.max(...amounts);
    const lastPayment = amounts[0];

    // Calculate trend based on last 3 payments
    let trend: 'up' | 'down' | 'stable' = 'stable';
    if (amounts.length >= 3) {
      const recentAvg = (amounts[0] + amounts[1] + amounts[2]) / 3;
      const oldAvg = amounts.length >= 6
        ? (amounts[3] + amounts[4] + amounts[5]) / 3
        : average;

      const diff = ((recentAvg - oldAvg) / oldAvg) * 100;
      if (diff > 5) trend = 'up';
      else if (diff < -5) trend = 'down';
    }

    return {
      average,
      min,
      max,
      lastPayment,
      count: relevantPayments.length,
      trend,
    };
  }

  /**
   * Get all services with payment history for estimation
   */
  getServicesWithHistory(): Array<{
    serviceId: string;
    serviceName: string;
    serviceLogo?: string;
    referencias: string[];
    paymentCount: number;
  }> {
    const recentPayments = this.getRecentPayments(50);
    const serviceMap = new Map<string, {
      serviceId: string;
      serviceName: string;
      serviceLogo?: string;
      referencias: Set<string>;
      paymentCount: number;
    }>();

    recentPayments.forEach(p => {
      if (p.status !== 'success') return;

      const existing = serviceMap.get(p.serviceId);
      if (existing) {
        existing.referencias.add(p.referencia);
        existing.paymentCount++;
      } else {
        serviceMap.set(p.serviceId, {
          serviceId: p.serviceId,
          serviceName: p.serviceName,
          serviceLogo: p.serviceLogo,
          referencias: new Set([p.referencia]),
          paymentCount: 1,
        });
      }
    });

    return Array.from(serviceMap.values()).map(s => ({
      ...s,
      referencias: Array.from(s.referencias),
    }));
  }
}

const paymentService = new PaymentServiceClient();
export default paymentService;
